import { take } from 'rxjs'
import { INgsiEntity, INgsiEntityIdAttrs, INgsiMapBaseEntity } from '../interfaces/ngsi-entity.interface'
import {
  IMapWidgetFormGroup,
  IMapWidgetFormGroupEntities,
  IMapWidgetFormGroupSettings
} from '../../../map-widget-settings/map-widget-settings.interface'
import {
  IDeleteSubscriptionArgs,
  INotification,
  ISubscriptionArgs,
  ISubscriptionArgsParams,
  NgsildConnectorService
} from 'ngsild-connector'
import { IExtendedAppPluginWidget } from '../../../../shared/interfaces/extended-app-plugin-widget.interface'
import { IMapWidgetPermissions } from '../../../../shared/interfaces/map-widget-permission.interface'
import { INgsiAIS } from '../interfaces/ngsi-ais.interface'
import { ICreateNgsiSubscriptionWidget, INgsiSubscriptionWidget, ModelService } from 'core-client/api'
import * as NgsiMaritimeTargets from '../models/ngsi-maritime-targets'
import * as NgsiAIS from '../models/ngsi-ais'
import * as NgsiPlannedPath from '../models/ngsi-planned-path'
import * as NgsiRadarTarget from '../models/ngsi-radar-target'
import * as NgsiFusionMaritimeTarget from '../models/ngsi-fusion-maritime-target'
import { ILeafletInstance } from '../../leaflet/leaflet.interface'
import * as leafletInstanceConfig from './leaflet-instance-config.constants'


// Backend service ngsild-proxy also defined
const GEQO_RADIUS = 100; // 15 km

export interface INgsiIdDesignPattern {
  prefix: string
  type: string
  owner: string
  idPattern: string
}
export const NgsiIdPrefix: string = `urn:ngsi-ld:`;

export const WAVELAB_ID: string = `AIS:CAPTN:211891460`;

export const geolocationSource = Object.values(leafletInstanceConfig.config.dataModel.AIS.dataSources)
         .map((geolocationSource: Record<string, string|string[]>) => Object.keys(geolocationSource)[0])

const geolocationSourceValues = Object.values(leafletInstanceConfig.config.dataModel.AIS.dataSources)
    .map((geolocationSource: Record<string, any>) => Object.values(geolocationSource)[0])

export const entityFulfillsClaim = (entity: INgsiEntity) => {
  return 'id' in entity && 'type' in entity && 'dateObserved' in entity && 'location' in entity;
}

export const updateVesselTargetPostion= (entity: INgsiAIS, settings: IMapWidgetFormGroupSettings): boolean => {
  const settingSource = getSelectedDataSourceTypeCategory(settings.dataSourceTypeCategory);
  const includedSources = entity.source === 'AI:VDM' || entity.source === 'IN:GLL' || entity.source === 'ST:LIVE';
  const isStatedSource = settingSource === entity.source;

  return includedSources && isStatedSource;
}

export const updatePredictionLine = (entity: INgsiAIS): boolean => {
  return entity.source === 'ST:PREDICT';
};

export const updateRateOfTurn = (entity: INgsiAIS): boolean => {
  return  entity.source === 'IN:ROT';
};

export const updateHdg = (entity: INgsiAIS): boolean => {
  return entity.source === 'IN:HDT';
}

export const updateSog = (entity: INgsiAIS): boolean => {
  return entity.source === 'IN:VBW';
}

export const isDateObservedToday= (entity: INgsiEntity) => {
  const timestamp = entity?.['dateObserved']?.['@value'];

  if (!timestamp) {
    return false;
  }

  const observedDate = new Date(timestamp);

  // Get the current date
  const now = new Date();

  // Compare year, month, and day
  return (
    observedDate.getFullYear() === now.getFullYear() &&
    observedDate.getMonth() === now.getMonth() &&
    observedDate.getDate() === now.getDate()
  )
}

export const filterNgsiEntityIdAttrs = (
  entityId: string,
  dataSourceIdentifier: string
): INgsiEntityIdAttrs => {
  let type = null;
  let owner = null;
  let idPattern: string | null = '';

  let id = entityId;
  if (entityId.includes(NgsiIdPrefix)) {
    id = entityId.replace(NgsiIdPrefix, '');
  }

  const arr = id.split(':');

  if (arr.length > 3) {
    arr.forEach((currentValue, index, arr) => {
      if (index === 0) {
        type = currentValue;
      } else if (index === 1) {
        owner = currentValue;
      } else if (index === 2 || index === arr.length - 1) {
        idPattern += `${currentValue}`;
      } else {
        idPattern += `:${currentValue}`;
      }
    })
  }

  if (arr.length === 3) {
    type = arr[0];
    owner = arr[1];
    idPattern = arr[2];
  }

  if (arr.length === 2) {
    type = arr[0];
    idPattern = arr[1];
  }

  if (arr.length === 1) {
    if (dataSourceIdentifier === leafletInstanceConfig.config.dataSourceSelector.MMSI.label) {
      type = leafletInstanceConfig.config.dataSourceSelector.MMSI.value.type;
      owner = leafletInstanceConfig.config.dataSourceSelector.MMSI.value.owner;
    }

    if (dataSourceIdentifier === leafletInstanceConfig.config.dataSourceSelector.TrackID.label) {
      type = leafletInstanceConfig.config.dataSourceSelector.TrackID.value.type;
      owner = leafletInstanceConfig.config.dataSourceSelector.TrackID.value.owner;
    }

    idPattern = arr[0];
  }

  return { prefix: NgsiIdPrefix, type: type, owner: owner, idPattern: idPattern}
}

/**
 *
 * ToDO:
 */
export const getSelectedDataSourceTypeCategory = (dataSourceTypeCategory: string): string => {
  if (dataSourceTypeCategory === geolocationSource[0]) {
    return `AI:VDM`;
  }

  if (dataSourceTypeCategory === geolocationSource[1]) {
    return `IN:${dataSourceTypeCategory}`;
  }

  if (dataSourceTypeCategory === geolocationSource[2]) {
    return 'ST:LIVE';
  }

  return `AI:VDM`;
}

export const appPluginWidgetHasEntityId = (
  entities: IMapWidgetFormGroupEntities[],
  entity: INgsiEntity
): boolean => {
  return entities.some((settingEntity: IMapWidgetFormGroupEntities) => settingEntity.id === entity.id.replace(NgsiIdPrefix, ''));
}

export const generateSubscriptionArgs = (
  uuid: string,
  appPluginWidgetId: string,
  entityIdAttrs: INgsiEntityIdAttrs[],
  isGeoQ: boolean = false
): ISubscriptionArgs => {
  let subscriptionArgs = { uuid : uuid, appPluginWidgetId: appPluginWidgetId } as ISubscriptionArgs;
  const params: ISubscriptionArgsParams[] = [];

  entityIdAttrs.forEach((entityIdAttr: INgsiEntityIdAttrs) => {
    if (entityIdAttr.type !== null && entityIdAttr.idPattern !== null) {
      let idPattern = String(entityIdAttr.idPattern);
      const entityType = String(entityIdAttr.type);

      const subArgs: ISubscriptionArgsParams = {
        type: entityType,
        idPattern: idPattern
      }

      if (entityIdAttr.owner) {
        subArgs.owner = String(entityIdAttr.owner);
      }

      if (isGeoQ) {
        subArgs.geoQ = {
          georel: `near;maxDistance==${GEQO_RADIUS}`,
          geometry: "Point",
          coordinates: []
        }
      }

      params.push(subArgs);
    }
  })

  if (params.length === 1) {
    subscriptionArgs.params = params[0];
  } else if (params.length > 0) {
    subscriptionArgs.params = params;
  }

  return subscriptionArgs;
}

export const formatIdsToNgsiIdFormat = (
  entityIDs: string[],
  selectedEntityIdsAttrs: INgsiEntityIdAttrs[]
): IMapWidgetFormGroupEntities[] => {
  const entities: IMapWidgetFormGroupEntities[] = []
  entityIDs.forEach((entityId: string) => {
    if (!entityId.includes(':')) {
      const entityIdAttr = selectedEntityIdsAttrs.find((entityIdAttr: INgsiEntityIdAttrs) => entityIdAttr.idPattern === entityId)
      if (entityIdAttr) {
        entityId = `${entityIdAttr.type}:${entityIdAttr.idPattern}`;

        if (entityIdAttr.owner) {
          entityId = `${entityIdAttr.type}:${entityIdAttr.owner}:${entityIdAttr.idPattern}`;
        }

        entities.push({ id: entityId });
      }
    } else {
      entities.push({ id: entityId });
    }
  })

  return entities;
}

export const formatSettingsEntities = (
  appPluginWidget: IExtendedAppPluginWidget,
  data: IMapWidgetFormGroup
) => {
  const selectedEntities = data.settings.entities as unknown as string[];
  const selectedEntityIdsAttrs = selectedEntities.map((entityId: string) =>
    filterNgsiEntityIdAttrs(entityId,data.settings.dataSourceIdentifier)
  );
  appPluginWidget.settings.entities = formatIdsToNgsiIdFormat(selectedEntities, selectedEntityIdsAttrs);

  return appPluginWidget;
}

export const isSingleEntity = (result: unknown) => {
  return Array.isArray(result) && result.length === 1;
}

export const hasMultipleEntities = (result: unknown) => {
  return Array.isArray(result) && result.length > 1;
}

export const emptyArray = (result: unknown) => {
  return Array.isArray(result) && result.length === 0;
}

export const isEntityInMapFocus = (
  settings: IMapWidgetFormGroupSettings,
  entity: INgsiMapBaseEntity | INgsiMapBaseEntity[]
) => {
  if (!settings.entities) {
    return false;
  }

  if (Array.isArray(entity)) {
    return settings.entities.some((settingsEntity: IMapWidgetFormGroupEntities) =>
      entity.some((nEntity: INgsiMapBaseEntity) =>
        nEntity.id === `${NgsiIdPrefix}${settingsEntity.id}`)
    );
  } else {
    return settings.entities.some((settingsEntity: IMapWidgetFormGroupEntities) =>
      entity.id === `${NgsiIdPrefix}${settingsEntity.id}`
    );
  }
}

export const filterIncomingNotifications = (
  notification: INotification,
  widgetPermissions: IMapWidgetPermissions,
  appPluginWidgetSettings: IMapWidgetFormGroupSettings
): boolean => {
  return widgetPermissions.ngsildConnectorEnabled &&
      filterOnEntityId(
      notification,
      appPluginWidgetSettings
      )
      || widgetPermissions.ngsildConnectorEnabled &&
      NgsiMaritimeTargets.filterOnMaritimeObjectDetection(
      notification,
      appPluginWidgetSettings,
      widgetPermissions
      )
      || widgetPermissions.ngsildConnectorEnabled &&
      NgsiPlannedPath.grantEntityPassage(
          notification,
          appPluginWidgetSettings,
      widgetPermissions
      )
      || widgetPermissions.ngsildConnectorEnabled &&
      NgsiRadarTarget.grantEntityPassage(
       notification,
       appPluginWidgetSettings,
       widgetPermissions
      )
      || widgetPermissions.ngsildConnectorEnabled &&
      NgsiFusionMaritimeTarget.grantEntityPassage(
          notification,
          appPluginWidgetSettings,
          widgetPermissions
      );
}

export const filterIncomingGeoQNotifications = (
  notification: INotification,
  widgetPermissions: IMapWidgetPermissions,
  appPluginWidgetSettings: IMapWidgetFormGroupSettings
): boolean => {
  return widgetPermissions.ngsildConnectorEnabled &&
    NgsiAIS.filterOnAISObjectDetection(
      notification,
      appPluginWidgetSettings,
      widgetPermissions
    ) &&
    !NgsiAIS.isNotificationOfConfiguredEntity(
      notification,
      appPluginWidgetSettings
    );
}

export const filterOnEntityId = (
  notification: INotification,
  appPluginWidgetSettings: IMapWidgetFormGroupSettings
): boolean => {
  const settings = appPluginWidgetSettings;
  if (settings?.entities) {
    return appPluginWidgetHasEntityId(settings.entities, notification.data[0]);
  } else {
    return false;
  }
}

export const manageNgsiSubscriptions = async (
  appPluginWidget: IExtendedAppPluginWidget,
  selectedEntityIdsAttrs: INgsiEntityIdAttrs[],
  widgetPermissions: IMapWidgetPermissions,
  ngsildConnectorService: NgsildConnectorService,
  modelService: ModelService
): Promise<void> => {
  const uuid = await ngsildConnectorService.uuidFromSettings();

  if (widgetPermissions.ngsildConnectorEnabled) {
    addSubscriptionForEntities(
        appPluginWidget,
        uuid,
        selectedEntityIdsAttrs,
        ngsildConnectorService,
        modelService
    );

    await addExtraSubscription(
        appPluginWidget,
        uuid,
        selectedEntityIdsAttrs,
        widgetPermissions,
        ngsildConnectorService,
        modelService
    );
  }
}

const manageMaritimeTargetsSubscription = async (
    ngsiExtraSubscription: INgsiSubscriptionWidget[],
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    widgetPermissions: IMapWidgetPermissions,
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
) => {
  // add/update subscription for 'MaritimeTargets'
  if (appPluginWidget.settings.enableMaritimeTargets && widgetPermissions.maritimeTargetsDataEnabled) {
    const existingSubToEntityType = ngsiExtraSubscription
    .find((ngsiExtraSubscription) => ngsiExtraSubscription.type === 'MaritimeTargets');

    const ngsiSubscriptionWidget = ngsiExtraSubscription
    .some((ngsiExtraSubscription: INgsiSubscriptionWidget) => ngsiExtraSubscription.app_plugin_widget === appPluginWidget._id &&
        ngsiExtraSubscription.type === 'MaritimeTargets');

    // Subscription for entityType already exist for widget
    if (ngsiSubscriptionWidget) {
      console.log(`Subscription for MaritimeTargets already exist.`)
      return;
    }

    // subscription for entityType already exist but is still missing for widget
    if (existingSubToEntityType != undefined && !ngsiSubscriptionWidget) {
      const ngsiSubscriptionWidget = {
        app_plugin_widget: appPluginWidget._id,
        subscriptionId: existingSubToEntityType.subscriptionId,
        type: existingSubToEntityType?.type,
        entityId: null,
        geoQSub: false,
        startPosition: null,
        lastKnowPosition: null,
        lastUpdated: new Date()
      } as unknown as ICreateNgsiSubscriptionWidget

      await modelService._ngsiSubscriptionWidget.upsertOne(ngsiSubscriptionWidget);
      return;
    }

    if (existingSubToEntityType == undefined && !ngsiSubscriptionWidget) {
      addMaritimeTargetsSubscription(appPluginWidget, uuid, ngsildConnectorService);
      return;
    }
  }

  // remove subscription for 'MaritimeTargets'
  if (!appPluginWidget.settings.enableMaritimeTargets && widgetPermissions.maritimeTargetsDataEnabled) {
    await removeMaritimeTargetsSubscription(
        appPluginWidget,
        uuid,
        ngsildConnectorService,
        modelService
    );
  }
}

const managePlannedPathSubscription = async (
    ngsiExtraSubscription: INgsiSubscriptionWidget[],
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    widgetPermissions: IMapWidgetPermissions,
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
) => {
  // add/update subscription for 'PlannedPath'
  if (appPluginWidget.settings.enablePlannedPath && widgetPermissions.plannedPathDataEnabled) {
    const existingSubToEntityType = ngsiExtraSubscription
    .find((ngsiExtraSubscription) => ngsiExtraSubscription.type === 'PlannedPath');

    const ngsiSubscriptionWidget = ngsiExtraSubscription
    .some((ngsiExtraSubscription: INgsiSubscriptionWidget) => ngsiExtraSubscription.app_plugin_widget === appPluginWidget._id &&
        ngsiExtraSubscription.type === 'PlannedPath');

    // Subscription for entityType already exist for widget
    if (ngsiSubscriptionWidget) {
      console.log(`Subscription for PlannedPath already exist.`)
      return;
    }

    // subscription for entityType already exist but is still missing for widget
    if (existingSubToEntityType != undefined && !ngsiSubscriptionWidget) {
      const ngsiSubscriptionWidget = {
        app_plugin_widget: appPluginWidget._id,
        subscriptionId: existingSubToEntityType.subscriptionId,
        type: existingSubToEntityType?.type,
        entityId: null,
        geoQSub: false,
        startPosition: null,
        lastKnowPosition: null,
        lastUpdated: new Date()
      } as unknown as ICreateNgsiSubscriptionWidget

      await modelService._ngsiSubscriptionWidget.upsertOne(ngsiSubscriptionWidget);
      return;
    }

    if (existingSubToEntityType == undefined && !ngsiSubscriptionWidget) {
      addPlannedPathSubscription(appPluginWidget, uuid, ngsildConnectorService);
      return;
    }
  }

  // remove subscription for 'PlannedPath'
  if (!appPluginWidget.settings.enablePlannedPath && widgetPermissions.plannedPathDataEnabled) {
    await removePlannedPathSubscription(
        appPluginWidget,
        uuid,
        ngsildConnectorService,
        modelService
    );
  }
}

const manageFusionMaritimeTargetSubscription = async (
    ngsiExtraSubscription: INgsiSubscriptionWidget[],
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    widgetPermissions: IMapWidgetPermissions,
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
) => {
  // add/update subscription for 'FusionMaritimeTarget'
  if (appPluginWidget.settings.enableFusionMaritimeTarget && widgetPermissions.fusionMaritimeTargetDataEnabled) {
    const existingSubToEntityType = ngsiExtraSubscription
    .find((ngsiExtraSubscription) => ngsiExtraSubscription.type === 'FusionMaritimeTarget');

    const ngsiSubscriptionWidget = ngsiExtraSubscription
    .some((ngsiExtraSubscription: INgsiSubscriptionWidget) => ngsiExtraSubscription.app_plugin_widget === appPluginWidget._id &&
        ngsiExtraSubscription.type === 'FusionMaritimeTarget');

    // Subscription for entityType already exist for widget
    if (ngsiSubscriptionWidget) {
      console.log(`Subscription for FusionMaritimeTarget already exist`);
      return;
    }

    // subscription for entityType already exist but is still missing for widget
    if (existingSubToEntityType != undefined && !ngsiSubscriptionWidget) {
      const ngsiSubscriptionWidget = {
        app_plugin_widget: appPluginWidget._id,
        subscriptionId: existingSubToEntityType.subscriptionId,
        type: existingSubToEntityType?.type,
        entityId: null,
        geoQSub: false,
        startPosition: null,
        lastKnowPosition: null,
        lastUpdated: new Date()
      } as unknown as ICreateNgsiSubscriptionWidget

      await modelService._ngsiSubscriptionWidget.upsertOne(ngsiSubscriptionWidget);
      return;
    }

    if (existingSubToEntityType == undefined && !ngsiSubscriptionWidget) {
      await addFusionMaritimeTarget(appPluginWidget, uuid, ngsildConnectorService);
      return;
    }
  }

  // remove subscription for 'FusionMaritimeTarget'
  if (!appPluginWidget.settings.enableFusionMaritimeTarget && widgetPermissions.fusionMaritimeTargetDataEnabled) {
    await removeFusionMaritimeTarget(
        appPluginWidget,
        uuid,
        ngsildConnectorService,
        modelService
    );
  }
}

const manageRadarTargetSubscription = async (
    ngsiExtraSubscription: INgsiSubscriptionWidget[],
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    widgetPermissions: IMapWidgetPermissions,
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
) => {
  // add/update subscription for 'RadarTarget'
  if (appPluginWidget.settings.enableRadarTarget && widgetPermissions.radarTargetDataEnabled) {
    const existingSubToEntityType = ngsiExtraSubscription
    .find((ngsiExtraSubscription) => ngsiExtraSubscription.type === 'RadarTarget');

    const ngsiSubscriptionWidget = ngsiExtraSubscription
    .some((ngsiExtraSubscription: INgsiSubscriptionWidget) => ngsiExtraSubscription.app_plugin_widget === appPluginWidget._id &&
        ngsiExtraSubscription.type === 'RadarTarget');

    // Subscription for entityType already exist for widget
    if (ngsiSubscriptionWidget) {
      console.log(`Subscription for RadarTarget already exist`);
      return;
    }

    // subscription for entityType already exist but is still missing for widget
    if (existingSubToEntityType != undefined && !ngsiSubscriptionWidget) {
      const ngsiSubscriptionWidget = {
        app_plugin_widget: appPluginWidget._id,
        subscriptionId: existingSubToEntityType.subscriptionId,
        type: existingSubToEntityType?.type,
        entityId: null,
        geoQSub: false,
        startPosition: null,
        lastKnowPosition: null,
        lastUpdated: new Date()
      } as unknown as ICreateNgsiSubscriptionWidget

      await modelService._ngsiSubscriptionWidget.upsertOne(ngsiSubscriptionWidget);
      return;
    }

    if (existingSubToEntityType == undefined && !ngsiSubscriptionWidget) {
      addRadarTargetSubscription(appPluginWidget, uuid, ngsildConnectorService);
      return;
    }
  }

  // remove subscription for 'RadarTarget'
  if (!appPluginWidget.settings.enableRadarTarget && widgetPermissions.radarTargetDataEnabled) {
    await removeRadarTargetSubscription(
        appPluginWidget,
        uuid,
        ngsildConnectorService,
        modelService
    );
  }
}

const manageAISGeoQSubscription = async (
    ngsiExtraSubscription: INgsiSubscriptionWidget[],
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    selectedEntityIdsAttrs: INgsiEntityIdAttrs[],
    widgetPermissions: IMapWidgetPermissions,
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
) => {
  // add/update subscription for 'AIS GeoQ'
  if (appPluginWidget.settings.enableAisTargets && widgetPermissions.aisTargetsDataEnabled) {
    const ngsiSubscriptionWidget = ngsiExtraSubscription
    .some((ngsiExtraSubscription: INgsiSubscriptionWidget) => ngsiExtraSubscription.app_plugin_widget === appPluginWidget._id);

    // Subscription for entityType already exist for widget
    if (ngsiSubscriptionWidget) {
      console.log(`Subscription for AIS GeoQ already exist`);
      return;
    }

    addAISGeoQSubscription(
        appPluginWidget,
        uuid,
        selectedEntityIdsAttrs,
        ngsildConnectorService
    );
  }

  if (!appPluginWidget.settings.enableAisTargets && widgetPermissions.aisTargetsDataEnabled) {
    await removeGeoQSubscriptions(
        appPluginWidget,
        uuid,
        ngsildConnectorService,
        modelService
    );
  }
}

const addExtraSubscription = async (
  appPluginWidget: IExtendedAppPluginWidget,
  uuid: string,
  selectedEntityIdsAttrs: INgsiEntityIdAttrs[],
  widgetPermissions: IMapWidgetPermissions,
  ngsildConnectorService: NgsildConnectorService,
  modelService: ModelService
): Promise<void> => {

  // List of all tracked NGSI map widget "extra" subscriptions
  const ngsiExtraTypeSubscriptions =
      await modelService._ngsiSubscriptionWidget.listAll(`type=PlannedPath&type=MaritimeTargets&type=RadarTarget&type=FusionMaritimeTarget`);

  // MaritimeTargets
  await manageMaritimeTargetsSubscription(
      ngsiExtraTypeSubscriptions,
      appPluginWidget,
      uuid,
      widgetPermissions,
      ngsildConnectorService,
      modelService
  );

  // PlannedPath
  await managePlannedPathSubscription(
      ngsiExtraTypeSubscriptions,
      appPluginWidget,
      uuid,
      widgetPermissions,
      ngsildConnectorService,
      modelService
  );

  // FusionMaritimeTarget
  await manageFusionMaritimeTargetSubscription(
      ngsiExtraTypeSubscriptions,
      appPluginWidget,
      uuid,
      widgetPermissions,
      ngsildConnectorService,
      modelService
  );

  // RadarTarget
  await manageRadarTargetSubscription(
      ngsiExtraTypeSubscriptions,
      appPluginWidget,
      uuid,
      widgetPermissions,
      ngsildConnectorService,
      modelService
  );

  const ngsiExtraGeoQSubscriptions =
      await modelService._ngsiSubscriptionWidget.listAll(`type=AIS&geoQSub=true`);

  await manageAISGeoQSubscription(
      ngsiExtraGeoQSubscriptions,
      appPluginWidget,
      uuid,
      selectedEntityIdsAttrs,
      widgetPermissions,
      ngsildConnectorService,
      modelService
  );
}

export const addSubscriptionForEntities = async (
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    filteredEntityIdsAttrs: INgsiEntityIdAttrs[],
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
): Promise<void> => {
  const selectedEntityIdsAttrs = appPluginWidget.settings.entities.map((entity: IMapWidgetFormGroupEntities) =>
      filterNgsiEntityIdAttrs(entity.id, appPluginWidget.settings.dataSourceIdentifier)
  );

  const ngsiAISSubscriptions =
      await modelService._ngsiSubscriptionWidget.listAll(`type=AIS&geoQSub=false`);

  let proceed = false;
  for (const entityIdsAttr of selectedEntityIdsAttrs) {
    const entityId = getWidgetEntityId(entityIdsAttr)
    if (entityId) {

      const subForEntityIdExist = ngsiAISSubscriptions.some((ngsiAISSubscription) =>
          ngsiAISSubscription.entityId === entityId && ngsiAISSubscription.app_plugin_widget === appPluginWidget._id
      );

      if (!subForEntityIdExist) {
        proceed = true;
      }
    }
  }

  if (!proceed) {
    console.log(`Subscriptions for ${appPluginWidget.settings?.dataSourceIdentifier} already exist`);
    return;
  }

  if (proceed) {
    const previousSubsArgs = generateSubscriptionArgs(
        uuid,
        appPluginWidget._id,
        filteredEntityIdsAttrs
    );
    const currentSubsArgs= generateSubscriptionArgs(
        uuid,
        appPluginWidget._id,
        selectedEntityIdsAttrs
    );

    if (currentSubsArgs.params !== undefined && previousSubsArgs.params !== undefined) {
      ngsildConnectorService.requestNotificationForEntity(currentSubsArgs, previousSubsArgs);
    } else if (currentSubsArgs.params !== undefined) {
      ngsildConnectorService.requestNotificationForEntity(currentSubsArgs);
    }
  }
}

export const addMaritimeTargetsSubscription = (
  appPluginWidget: IExtendedAppPluginWidget,
  uuid: string,
  ngsildConnectorService: NgsildConnectorService
): void => {
  let subArgs = { uuid : uuid, appPluginWidgetId: appPluginWidget._id } as ISubscriptionArgs;
  subArgs.params = {
    type: NgsiMaritimeTargets.ngsiMaritimeTargetsIdPattern.type,
    owner: NgsiMaritimeTargets.ngsiMaritimeTargetsIdPattern.owner,
    idPattern: null
  }

  ngsildConnectorService.requestNotificationForEntity(subArgs);
}

export const addAISGeoQSubscription = (
  appPluginWidget: IExtendedAppPluginWidget,
  uuid: string,
  filteredEntityIdsAttrs: INgsiEntityIdAttrs[],
  ngsildConnectorService: NgsildConnectorService
): void => {
  const selectedEntityIdsAttrs = appPluginWidget.settings.entities.map(
    (entity: IMapWidgetFormGroupEntities) =>
      filterNgsiEntityIdAttrs(entity.id, appPluginWidget.settings.dataSourceIdentifier)
  );
  const previousSubsArgs = generateSubscriptionArgs(
    uuid,
    appPluginWidget._id,
    filteredEntityIdsAttrs,
    true
  );
  const currentSubsArgs= generateSubscriptionArgs(
    uuid,
    appPluginWidget._id,
    selectedEntityIdsAttrs,
    true
  );

  if (currentSubsArgs.params !== undefined && previousSubsArgs.params !== undefined) {
    ngsildConnectorService.requestNotificationForEntity(currentSubsArgs, previousSubsArgs);
  } else if (currentSubsArgs.params !== undefined) {
    ngsildConnectorService.requestNotificationForEntity(currentSubsArgs);
  }
}

const addPlannedPathSubscription = (
  appPluginWidget: IExtendedAppPluginWidget,
  uuid: string,
  ngsildConnectorService: NgsildConnectorService
)=> {
  let subArgs = { uuid : uuid, appPluginWidgetId: appPluginWidget._id } as ISubscriptionArgs;
  subArgs.params = {
    type: NgsiPlannedPath.ngsiPlannedPathPattern.type,
    owner: NgsiPlannedPath.ngsiPlannedPathPattern.owner,
    idPattern: null
  }

  ngsildConnectorService.requestNotificationForEntity(subArgs);
}

const addRadarTargetSubscription = (
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    ngsildConnectorService: NgsildConnectorService
)=> {
  let subArgs = { uuid : uuid, appPluginWidgetId: appPluginWidget._id } as ISubscriptionArgs;
  subArgs.params = {
    type: NgsiRadarTarget.ngsiRadarTargetsIdPattern.type,
    owner: NgsiRadarTarget.ngsiRadarTargetsIdPattern.owner,
    idPattern: null
  }

  ngsildConnectorService.requestNotificationForEntity(subArgs);
}

export const removeMaritimeTargetsSubscription = async (
  appPluginWidget: IExtendedAppPluginWidget,
  uuid: string,
  ngsildConnectorService: NgsildConnectorService,
  modelService: ModelService
): Promise<void> => {
  const ngsiSubscriptionWidgets = await modelService._ngsiSubscriptionWidget.listAll(`app_plugin_widget=${appPluginWidget._id}`);
  const maritimeTypesSubscriptions = ngsiSubscriptionWidgets
    .find((ngsiSubscriptionWidget: INgsiSubscriptionWidget) =>
      ngsiSubscriptionWidget.type === NgsiMaritimeTargets.entityType);

  if (maritimeTypesSubscriptions) {
    let subArgs = {
      uuid: uuid,
      appPluginWidgetId: appPluginWidget._id,
      subscriptions: [{
        ngsiSubscriptionWidgetId: maritimeTypesSubscriptions._id,
        subscriptionId: maritimeTypesSubscriptions.subscriptionId
      }]
    } as unknown as IDeleteSubscriptionArgs;

    ngsildConnectorService.deleteSubscription$(subArgs)
      .pipe(take(1))
      .subscribe()
  }
}

const removePlannedPathSubscription = async (
  appPluginWidget: IExtendedAppPluginWidget,
  uuid: string,
  ngsildConnectorService: NgsildConnectorService,
  modelService: ModelService
): Promise<void> => {
  const ngsiSubscriptionWidgets = await modelService._ngsiSubscriptionWidget.listAll(`app_plugin_widget=${appPluginWidget._id}`);
  const plannedPathTypeSubscriptions = ngsiSubscriptionWidgets
    .find((ngsiSubscriptionWidget: INgsiSubscriptionWidget) =>
      ngsiSubscriptionWidget.type === NgsiPlannedPath.plannedPathType);

  if (plannedPathTypeSubscriptions) {
    let subArgs = {
      uuid: uuid,
      appPluginWidgetId: appPluginWidget._id,
      subscriptions: [{
        ngsiSubscriptionWidgetId: plannedPathTypeSubscriptions._id,
        subscriptionId: plannedPathTypeSubscriptions.subscriptionId
      }]
    } as unknown as IDeleteSubscriptionArgs;

    ngsildConnectorService.deleteSubscription$(subArgs)
      .pipe(take(1))
      .subscribe()
  }
}

const removeRadarTargetSubscription = async (
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
) => {
  const ngsiSubscriptionWidgets = await modelService._ngsiSubscriptionWidget.listAll(`app_plugin_widget=${appPluginWidget._id}`);
  const radarTargetTypeSubscriptions = ngsiSubscriptionWidgets
      .find((ngsiSubscriptionWidget: INgsiSubscriptionWidget) =>
          ngsiSubscriptionWidget.type === NgsiRadarTarget.entityType);

  if (radarTargetTypeSubscriptions) {
    let subArgs = {
      uuid: uuid,
      appPluginWidgetId: appPluginWidget._id,
      subscriptions: [{
        ngsiSubscriptionWidgetId: radarTargetTypeSubscriptions._id,
        subscriptionId: radarTargetTypeSubscriptions.subscriptionId
      }]
    } as unknown as IDeleteSubscriptionArgs;

    ngsildConnectorService.deleteSubscription$(subArgs)
        .pipe(take(1))
        .subscribe()
  }
}

const addFusionMaritimeTarget = async(
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    ngsildConnectorService: NgsildConnectorService
)  => {
  let subArgs = { uuid : uuid, appPluginWidgetId: appPluginWidget._id } as ISubscriptionArgs;
  subArgs.params = {
    type: NgsiFusionMaritimeTarget.ngsiFusionTargetsIdPattern.type,
    owner: NgsiFusionMaritimeTarget.ngsiFusionTargetsIdPattern.owner,
    idPattern: null
  }

  ngsildConnectorService.requestNotificationForEntity(subArgs);
}

const removeFusionMaritimeTarget = async(
    appPluginWidget: IExtendedAppPluginWidget,
    uuid: string,
    ngsildConnectorService: NgsildConnectorService,
    modelService: ModelService
)  => {
  const ngsiSubscriptionWidgets = await modelService._ngsiSubscriptionWidget.listAll(`app_plugin_widget=${appPluginWidget._id}`);
  const fusionMaritimeTargetTypeSubscriptions = ngsiSubscriptionWidgets
      .find((ngsiSubscriptionWidget: INgsiSubscriptionWidget) =>
          ngsiSubscriptionWidget.type === NgsiFusionMaritimeTarget.entityType);

  if (fusionMaritimeTargetTypeSubscriptions) {
    let subArgs = {
      uuid: uuid,
      appPluginWidgetId: appPluginWidget._id,
      subscriptions: [{
        ngsiSubscriptionWidgetId: fusionMaritimeTargetTypeSubscriptions._id,
        subscriptionId: fusionMaritimeTargetTypeSubscriptions.subscriptionId
      }]
    } as unknown as IDeleteSubscriptionArgs;

    ngsildConnectorService.deleteSubscription$(subArgs)
        .pipe(take(1))
        .subscribe()
  }
}

export const removeGeoQSubscriptions = async (
  appPluginWidget: IExtendedAppPluginWidget,
  uuid: string,
  ngsildConnectorService: NgsildConnectorService,
  modelService: ModelService
) => {
  const ngsiSubscriptionWidgets = await modelService._ngsiSubscriptionWidget.listAll(`app_plugin_widget=${appPluginWidget._id}`);
  const activeGeoQAISSubs = ngsiSubscriptionWidgets
    .filter((ngsiSubscriptionWidget: INgsiSubscriptionWidget) => ngsiSubscriptionWidget?.entityId != undefined && ngsiSubscriptionWidget.geoQSub);

  if (activeGeoQAISSubs) {
    let subArgs = {
      uuid: uuid,
      appPluginWidgetId: appPluginWidget._id,
      subscriptions: []
    } as unknown as IDeleteSubscriptionArgs;

    for (const geoQSub of activeGeoQAISSubs) {
      subArgs.subscriptions.push({
        ngsiSubscriptionWidgetId: geoQSub._id,
        subscriptionId: geoQSub.subscriptionId
      })
    }

    ngsildConnectorService.deleteSubscription$(subArgs)
      .pipe(take(1))
      .subscribe()
  }
}

export const showUpdateTempDataOnMap = (
    entity: INgsiMapBaseEntity,
    instance: ILeafletInstance
): boolean => {
  if (entity.type === instance.dataConfig.dataModel.AIS.type) {
    return instance.dataConfig.dataModel.AIS?.showTempLayer || false;
  }

  if (entity.type === instance.dataConfig.dataModel.MaritimeTargets.type) {
    return instance.dataConfig.dataModel.MaritimeTargets.showDefaultLayer;
  }

  if (entity.type === instance.dataConfig.dataModel.RadarTarget.type) {
    return instance.dataConfig.dataModel.RadarTarget.showDefaultLayer;
  }

  if (entity.type === instance.dataConfig.dataModel.FusionMaritimeTarget.type) {
    return instance.dataConfig.dataModel.FusionMaritimeTarget.showDefaultLayer;
  }

  return false;
}

const getWidgetEntityId = (entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): string | undefined => {
  if (!entityId?.idPattern) {
    return undefined;
  }

  return entityId?.owner ?
      `${NgsiIdPrefix}${entityId?.type}:${entityId?.owner}:${entityId?.idPattern}` :
      `${NgsiIdPrefix}${entityId?.type}:${entityId?.idPattern}`;
}
